import React from "react";
import { FileText } from "lucide-react";
import { formatCurrency, formatDate, formatKey, formatValue } from "../utils/responseParser";

/**
 * InvoiceCard - Renders invoice data in a structured card format
 * @param {Object} props
 * @param {Object} props.data - The invoice data object
 * @param {Function} props.onClick - Optional callback when card is clicked
 * @returns {JSX.Element}
 */
const InvoiceCard = ({ data, onClick }) => {
    if (!data || typeof data !== "object") return null;

    // Payment status color mapping
    const getPaymentStatusColor = (status) => {
        if (!status) return "bg-gray-100 text-gray-700";
        const s = status.toLowerCase();
        if (s.includes("paid") && !s.includes("partial") && !s.includes("over")) return "bg-green-100 text-green-700";
        if (s.includes("unpaid") || s.includes("overdue")) return "bg-red-100 text-red-700";
        if (s.includes("partial") || s.includes("progress")) return "bg-amber-100 text-amber-700";
        if (s.includes("over")) return "bg-purple-100 text-purple-700";
        return "bg-gray-100 text-gray-700";
    };

    // Extract common invoice fields
    const invoiceNumber = data.invoiceNumber || data.invoice_number || data.invoiceId || data.invoice_id || data.id || "N/A";
    const paymentStatus = data.paymentStatus || data.payment_status || data.payment || data.status || "Unknown";
    const invoiceStatus = data.invoiceStatus || data.invoice_status || data.approvalStatus || data.approval_status || "-";
    const totalAmount = data.totalAmount || data.total_amount || data.amount || data.total || null;
    const currency = data.currency || "USD";
    const invoiceDate = data.invoiceDate || data.invoice_date || data.date || data.issuedDate || data.issued_date || null;
    const billToCompany = data.billToCompany || data.bill_to_company || data.billTo || data.bill_to || data.customer || data.client || null;
    const payToCompany = data.payToCompany || data.pay_to_company || data.payTo || data.pay_to || data.vendor || data.supplier || null;

    // Extract shipments if available - handle both array and JSON string
    let shipments = data.shipments || data.shipmentList || data.shipment_list || [];
    // Parse shipments if it's a JSON string
    if (typeof shipments === "string") {
        try {
            shipments = JSON.parse(shipments);
        } catch {
            shipments = [];
        }
    }
    // Ensure shipments is an array
    if (!Array.isArray(shipments)) {
        // If it's a single object, wrap it in an array
        if (shipments && typeof shipments === "object") {
            shipments = [shipments];
        } else {
            shipments = [];
        }
    }

    // Get additional fields to display
    const getAdditionalFields = () => {
        const excludeKeys = [
            "invoiceNumber", "invoice_number", "invoiceId", "invoice_id", "id",
            "paymentStatus", "payment_status", "payment", "status",
            "invoiceStatus", "invoice_status", "approvalStatus", "approval_status",
            "totalAmount", "total_amount", "amount", "total",
            "currency",
            "invoiceDate", "invoice_date", "date", "issuedDate", "issued_date",
            "billToCompany", "bill_to_company", "billTo", "bill_to", "customer", "client",
            "payToCompany", "pay_to_company", "payTo", "pay_to", "vendor", "supplier",
            "shipments", "shipmentList", "shipment_list"
        ];
        const additional = {};
        for (const [key, value] of Object.entries(data)) {
            if (!excludeKeys.includes(key) && value !== null && value !== undefined) {
                additional[key] = value;
            }
        }
        return additional;
    };

    const additionalFields = getAdditionalFields();

    return (
        <div
            className={`mt-3 rounded-lg border border-gray-200 bg-white overflow-hidden transition-all ${
                onClick ? "cursor-pointer hover:shadow-md hover:border-acs-primary-green" : ""
            }`}
            onClick={() => onClick && onClick(data)}
        >
            {/* Header */}
            <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200">
                <FileText className="w-4 h-4 text-acs-primary-green" />
                <span className="text-sm font-semibold text-gray-800 flex-1">
                    Invoice {invoiceNumber}
                </span>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${getPaymentStatusColor(paymentStatus)}`}>
                    {paymentStatus}
                </span>
            </div>

            {/* Body */}
            <div className="p-3">
                {/* Summary */}
                <div className="flex gap-4 mb-3">
                    <div className="flex-1">
                        <span className="text-[10px] text-gray-500 uppercase tracking-wide">Total</span>
                        <div className="text-xl font-bold text-acs-primary-green">
                            {formatCurrency(totalAmount, currency)}
                        </div>
                    </div>
                    {invoiceStatus && invoiceStatus !== "-" && (
                        <div className="flex-1">
                            <span className="text-[10px] text-gray-500 uppercase tracking-wide">Status</span>
                            <div className="text-base font-semibold text-gray-800">{invoiceStatus}</div>
                        </div>
                    )}
                </div>

                {/* Details */}
                <div className="space-y-1.5">
                    {invoiceDate && (
                        <div className="flex justify-between text-xs">
                            <span className="text-gray-500">Invoice Date</span>
                            <span className="font-medium text-gray-800">{formatDate(invoiceDate)}</span>
                        </div>
                    )}
                    {billToCompany && (
                        <div className="flex justify-between text-xs">
                            <span className="text-gray-500">Bill To</span>
                            <span className="font-medium text-gray-800">{billToCompany}</span>
                        </div>
                    )}
                    {payToCompany && (
                        <div className="flex justify-between text-xs">
                            <span className="text-gray-500">Pay To</span>
                            <span className="font-medium text-gray-800">{payToCompany}</span>
                        </div>
                    )}
                </div>

                {/* Additional fields */}
                {Object.keys(additionalFields).length > 0 && (
                    <div className="mt-2 pt-2 border-t border-gray-100 space-y-1">
                        {Object.entries(additionalFields).map(([key, value]) => (
                            <div key={key} className="flex justify-between text-xs">
                                <span className="text-gray-500">{formatKey(key)}</span>
                                <span className="font-medium text-gray-800">{formatValue(value)}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Shipments list */}
                {shipments && shipments.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                        <span className="text-[10px] text-gray-500 uppercase tracking-wide block mb-2">
                            Shipments ({shipments.length})
                        </span>
                        <div className="space-y-1.5">
                            {shipments.map((shipment, idx) => {
                                const shipmentNum = shipment.shipmentNumber || shipment.shipment_number || shipment.id || idx;
                                const routeOrigin = shipment.origin || shipment.originLocation || shipment.origin_location || "-";
                                const routeDest = shipment.destination || shipment.destinationLocation || shipment.destination_location || "-";
                                const charge = shipment.chargeAmount || shipment.charge_amount || shipment.amount || shipment.charge || null;

                                return (
                                    <div
                                        key={idx}
                                        className="flex items-center gap-2 p-2 bg-gray-50 rounded text-xs"
                                    >
                                        <span className="font-semibold text-gray-800 min-w-[80px]">{shipmentNum}</span>
                                        <span className="flex-1 text-gray-600 truncate">
                                            {routeOrigin} → {routeDest}
                                        </span>
                                        {charge && (
                                            <span className="font-medium text-gray-800">
                                                {formatCurrency(charge, currency)}
                                            </span>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default InvoiceCard;
